import React, { useMemo, useState } from "react";
import { ArrowDownWideNarrow, ArrowUpNarrowWide } from "lucide-react";

export const LABOUR_SHARE = [
  { code: "au", it: "Australia", en: "Australia", peak: 71.9, peakYear: 1974, latest: 54.6, change: -17.3 },
  { code: "nl", it: "Paesi Bassi", en: "Netherlands", peak: 73.1, peakYear: 1975, latest: 55.9, change: -17.2 },
  { code: "it", it: "Italia", en: "Italy", peak: 62.6, peakYear: 1975, latest: 51.2, change: -11.4 },
  { code: "se", it: "Svezia", en: "Sweden", peak: 72.0, peakYear: 1977, latest: 61.0, change: -11.0 },
  { code: "ca", it: "Canada", en: "Canada", peak: 75.8, peakYear: 1972, latest: 65.9, change: -9.9 },
  { code: "fr", it: "Francia", en: "France", peak: 71.5, peakYear: 1980, latest: 63.1, change: -8.4 },
  { code: "us", it: "Stati Uniti", en: "United States", peak: 64.9, peakYear: 1970, latest: 56.8, change: -8.1 },
  { code: "nz", it: "Nuova Zelanda", en: "New Zealand", peak: 62.9, peakYear: 1982, latest: 55.4, partial: true, change: -7.4 },
  { code: "es", it: "Spagna", en: "Spain", peak: 66.7, peakYear: 1980, latest: 59.5, partial: true, change: -7.2 },
  { code: "de", it: "Germania", en: "Germany", peak: 69.4, peakYear: 1981, latest: 63.7, partial: true, change: -5.6 },
  { code: "gb", it: "Regno Unito", en: "United Kingdom", peak: 64.8, peakYear: 1975, latest: 59.5, change: -5.3 },
  { code: "kr", it: "Corea del Sud", en: "South Korea", peak: 59.0, peakYear: 1996, latest: 54.6, change: -4.4 },
  { code: "jp", it: "Giappone", en: "Japan", peak: 62.1, peakYear: 1977, latest: 58.8, change: -3.3 },
].map(d => ({ ...d, rel: +((d.change / d.peak) * 100).toFixed(1) }));

const T = {
  it: {
    title: "La quota del lavoro è scesa in tutti e tredici i paesi, da 3 a 17 punti",
    sub: "Redditi da lavoro in percentuale del PIL. Grigio: massimo dal 1970 con il suo anno. Blu: ultimo dato (2023).",
    sort: "Ordina per", change: "Variazione", latest: "Ultimo dato", peak: "Massimo", peakYear: "Anno del massimo", name: "Paese",
    colChange: "variazione, punti", peakLbl: "Massimo", latestLbl: "2023", pts: "punti", rel: "in termini relativi",
    note: "* Serie effettiva solo dal 1980-82: il massimo reale può essere precedente.",
    source: "Fonte: Penn World Table 11.0 via FRED, quota dei redditi da lavoro sul PIL a prezzi correnti.",
    hint: "Passa il mouse o tocca un paese per i dettagli.",
  },
  en: {
    title: "The labour share fell in all thirteen countries, by 3 to 17 points",
    sub: "Labour income as % of GDP. Grey: post-1970 peak with its year. Blue: latest data (2023).",
    sort: "Sort by", change: "Change", latest: "Latest", peak: "Peak", peakYear: "Peak year", name: "Country",
    colChange: "change, points", peakLbl: "Peak", latestLbl: "2023", pts: "points", rel: "in relative terms",
    note: "* Series only available from 1980-82: the true peak may be earlier.",
    source: "Source: Penn World Table 11.0 via FRED, labour income share of GDP at current prices.",
    hint: "Hover or tap a country for details.",
  },
};

const MIN = 48, MAX = 80, ROW = 34;
const pct = v => `${((v - MIN) / (MAX - MIN)) * 100}%`;
const fmt = (v, lang) => { const str = Number(v).toFixed(1); return lang === "it" ? str.replace(".", ",") : str; };

const SORTS = ["change", "latest", "peak", "peakYear", "name"];

export const LabourShareChart = ({ lang = "it" }) => {
  const ui = T[lang] ? lang : "en";
  const t = T[ui];
  const [sortKey, setSortKey] = useState("change");
  const [asc, setAsc] = useState(true);
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);

  const order = useMemo(() => {
    const rows = [...LABOUR_SHARE].sort((a, b) => sortKey === "name" ? a[ui].localeCompare(b[ui]) : a[sortKey] - b[sortKey]);
    if (!asc) rows.reverse();
    return Object.fromEntries(rows.map((d, i) => [d.code, i]));
  }, [sortKey, asc, ui]);

  const activeCode = hover || pinned;
  const active = LABOUR_SHARE.find(d => d.code === activeCode);

  const onSort = key => {
    if (key === sortKey) setAsc(a => !a); else { setSortKey(key); setAsc(key !== "latest" && key !== "peak"); }
  };

  return (
    <section className="lsc" data-testid="labour-share-chart" dir="ltr">
      <h3 className="lsc-title">{t.title}</h3>
      <p className="lsc-sub">{t.sub}</p>
      <div className="lsc-toolbar" role="toolbar" aria-label={t.sort}>
        <span className="lsc-toolbar-label">{t.sort}</span>
        {SORTS.map(k => (
          <button key={k} type="button" className={`lsc-sort ${sortKey === k ? "active" : ""}`} onClick={() => onSort(k)} data-testid={`lsc-sort-${k}`} aria-pressed={sortKey === k}>
            {t[k]} {sortKey === k && (asc ? <ArrowUpNarrowWide size={12} /> : <ArrowDownWideNarrow size={12} />)}
          </button>
        ))}
      </div>
      <div className="lsc-axis" aria-hidden="true">
        {[50, 60, 70].map(v => <span key={v} style={{ left: pct(v) }}>{v}%</span>)}
        <span className="lsc-axis-change">{t.colChange}</span>
      </div>
      <div className="lsc-body" style={{ height: LABOUR_SHARE.length * ROW }} onMouseLeave={() => setHover(null)}>
        <div className="lsc-gridlayer" aria-hidden="true">
          {[50, 60, 70, 80].map(v => <div key={v} className="lsc-grid" style={{ left: pct(v) }} />)}
        </div>
        {LABOUR_SHARE.map(d => {
          const isActive = activeCode === d.code;
          return (
            <div key={d.code} className={`lsc-row ${isActive ? "active" : ""} ${d.code === "it" ? "home" : ""}`}
              style={{ transform: `translateY(${order[d.code] * ROW}px)` }}
              onMouseEnter={() => setHover(d.code)} onFocus={() => setHover(d.code)} onBlur={() => setHover(null)}
              onClick={() => setPinned(p => (p === d.code ? null : d.code))} tabIndex={0} role="button"
              aria-label={`${d[ui]}: ${d.peak}% (${d.peakYear}) → ${d.latest}%, ${d.change}`} data-testid={`lsc-row-${d.code}`}>
              <span className="lsc-name">{d[ui]}</span>
              <div className="lsc-track">
                <span className="lsc-bar" style={{ left: pct(d.latest), width: `calc(${pct(d.peak)} - ${pct(d.latest)})` }} />
                <span className="lsc-dot peak" style={{ left: pct(d.peak) }} />
                <span className="lsc-dot latest" style={{ left: pct(d.latest) }} />
                <span className="lsc-val latest" style={{ left: pct(d.latest) }}>{fmt(d.latest, ui)}</span>
                <span className="lsc-val peak" style={{ left: pct(d.peak) }}>{fmt(d.peak, ui)} · {d.peakYear}{d.partial ? "*" : ""}</span>
              </div>
              <span className="lsc-change">{fmt(d.change, ui)}</span>
            </div>
          );
        })}
        {active && (
          <div className="lsc-tooltip" role="status" data-testid="lsc-tooltip"
            style={{ transform: `translateY(${order[active.code] * ROW + ROW}px)` }}>
            <strong>{active[ui]}</strong>
            <span>{t.peakLbl} {active.peakYear}{active.partial ? "*" : ""}: <b>{fmt(active.peak, ui)}%</b></span>
            <span>{t.latestLbl}: <b>{fmt(active.latest, ui)}%</b></span>
            <span className="lsc-tooltip-change">{fmt(active.change, ui)} {t.pts} · {fmt(active.rel, ui)}% {t.rel}</span>
          </div>
        )}
      </div>
      <p className="lsc-hint">{t.hint}</p>
      <p className="lsc-note">{t.note}</p>
      <p className="lsc-source">{t.source}</p>
    </section>
  );
};

export default LabourShareChart;
